import { useEffect, useId, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import type { TAccountStatus, TAdminUser } from '@schemas'

import { useCurrentUser, useListAdminUsers } from '@/api/hooks'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { AccountStatusBadge, formatDate } from './account-components'
import { UserAccountSheet } from './UserAccountSheet'
import { InviteUserDialog } from './InviteUserDialog'

type StatusFilter = TAccountStatus | 'all'
type RoleFilter = 'all' | 'admin' | 'standard'

export function AdminUsersPage() {
  const currentUserQuery = useCurrentUser()
  const currentUser = currentUserQuery.data
  const statusId = useId()
  const roleId = useId()

  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [role, setRole] = useState<RoleFilter>('all')
  const [page, setPage] = useState(1)
  const [opened, setOpened] = useState<TAdminUser | null>(null)
  const [isInviteOpen, setIsInviteOpen] = useState(false)

  const usersQuery = useListAdminUsers({
    search: searchTerm || undefined,
    status: status === 'all' ? undefined : status,
    admin: role === 'all' ? undefined : role === 'admin',
    page,
  })
  const users = useMemo(() => usersQuery.data?.users ?? [], [usersQuery.data])
  // The sheet shows the newest copy of its account: the listed row, or what the last change
  // returned. A change can move the account out of the filtered or searched list.
  const listed = opened && users.find(user => user.id === opened.id)
  const selected = listed && listed.updatedAt >= opened.updatedAt ? listed : opened

  const total = usersQuery.data?.total ?? 0
  const pageSize = usersQuery.data?.pageSize ?? 20
  const pageCount = Math.max(1, pageSize > 0 ? Math.ceil(total / pageSize) : 1)
  // Deleting, disabling or changing an account can shrink the list below the current page;
  // move back to its last page instead of showing an empty one with no pager.
  useEffect(() => {
    if (usersQuery.data && page > pageCount) setPage(pageCount)
  }, [usersQuery.data, page, pageCount])

  if (currentUserQuery.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Skeleton className="h-16 w-16 rounded-full" />
      </div>
    )
  }

  if (!currentUser?.isAdmin) {
    return <Navigate to="/" replace />
  }

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setPage(1)
    setSearchTerm(searchInput.trim())
  }

  return (
    <div className="flex flex-col gap-6" data-testid="admin-users-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">User administration</h1>
          <p className="text-sm text-muted-foreground">
            Invite staff, give them setup or reset links, and manage their access.
          </p>
        </div>
        <Button type="button" onClick={() => setIsInviteOpen(true)}>
          Invite user
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Directory</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <form className="flex flex-wrap items-center gap-2" onSubmit={handleSearchSubmit}>
              <Input
                aria-label="Search name or email"
                value={searchInput}
                onChange={event => setSearchInput(event.target.value)}
                placeholder="Search name or email…"
                className="w-full max-w-xs"
              />
              <Button type="submit" variant="outline">
                Search
              </Button>
              {searchTerm ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setSearchInput('')
                    setSearchTerm('')
                    setPage(1)
                  }}
                >
                  Clear
                </Button>
              ) : null}
            </form>
            <div className="space-y-1">
              <label htmlFor={statusId} className="block text-xs font-medium">
                Status
              </label>
              <select
                id={statusId}
                className="h-9 rounded-md border bg-background px-3 text-sm"
                value={status}
                onChange={event => {
                  setStatus(event.target.value as StatusFilter)
                  setPage(1)
                }}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="invited">Invited</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor={roleId} className="block text-xs font-medium">
                Access
              </label>
              <select
                id={roleId}
                className="h-9 rounded-md border bg-background px-3 text-sm"
                value={role}
                onChange={event => {
                  setRole(event.target.value as RoleFilter)
                  setPage(1)
                }}
              >
                <option value="all">All</option>
                <option value="admin">Administrators</option>
                <option value="standard">Standard</option>
              </select>
            </div>
          </div>

          {usersQuery.isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {usersQuery.error?.message ?? 'Unable to load users.'}
            </div>
          ) : usersQuery.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-12 w-full" />
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="rounded-md border border-dashed border-muted p-6 text-center text-sm text-muted-foreground">
              No users found.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Access</TableHead>
                  <TableHead>Last sign-in</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(user => (
                  <TableRow key={user.id}>
                    <TableCell>{user.person?.name ?? 'Not linked'}</TableCell>
                    <TableCell className="font-medium break-all">{user.email}</TableCell>
                    <TableCell>
                      <AccountStatusBadge user={user} />
                    </TableCell>
                    <TableCell>
                      {user.isAdmin ? (
                        <Badge variant="secondary">Administrator</Badge>
                      ) : user.showCount ? (
                        `${user.showCount} show${user.showCount === 1 ? '' : 's'}`
                      ) : (
                        <span className="text-muted-foreground">No show access</span>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(user.lastLoginAt)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={`Manage ${user.email}`}
                        onClick={() => setOpened(user)}
                      >
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {pageCount > 1 ? (
            <div className="flex items-center justify-end gap-3">
              <span className="text-sm text-muted-foreground">
                Page {page} of {pageCount}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPage(prev => Math.min(pageCount, prev + 1))}
                disabled={page >= pageCount}
              >
                Next
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {selected ? (
        <UserAccountSheet
          key={selected.id}
          user={selected}
          isSelf={selected.id === currentUser.id}
          onChanged={setOpened}
          onClose={() => setOpened(null)}
        />
      ) : null}
      {isInviteOpen ? <InviteUserDialog onClose={() => setIsInviteOpen(false)} /> : null}
    </div>
  )
}

export default AdminUsersPage
